import { Component, ElementRef, OnDestroy, OnInit, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, UntypedFormGroup, NgControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AddressService } from '../../services/address.service';
import { ErrorMessageService } from '../../services/error-message.service';
import { TextListInputService } from '../../services/text-list-input.service';
import { ViewportService } from '../../services/viewport.service';
import { AddressInterface } from '../../interfaces/address.interface';
import { PossibleAddressInterface } from '../../interfaces/possible-address.interface';
import { TableColumnInterface } from '../../interfaces/table-column.interface';
import { zipCodeStringParser } from '../../utils/zip-code-string-parser.util';

@Component({
  selector: 'app-address-input',
  templateUrl: './address-input.component.html',
  styleUrls: ['./address-input.component.scss'],
  providers: [AddressService]
})
export class AddressInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @ViewChild('closeModal') closeModal: ElementRef;
  private readonly zipCodeFullLength = 9;
  private readonly minLocationLength = 3;
  private lastSearchedZipCode: string;
  private lastSearchedLocation: string;
  private zipCodeSearchTimeout: any;
  private formValueChangesSubscription: Subscription;
  private zipCodeSearchFormValueChangesSubscription: Subscription;
  private clearTextListInputPossibleValuesSubscription: Subscription;
  private isDesktopSubscription: Subscription;
  public isDesktop: boolean;
  public formGroup: UntypedFormGroup;
  public zipCodeSearchFormGroup: UntypedFormGroup;
  public value: AddressInterface;
  public addressTypes: string[];
  public statesList: string[];
  public autoFilledFields: Record<string, boolean> = {};
  public zipCodeSearchResults: PossibleAddressInterface[];
  public zipCodeSearchResultsColumns: TableColumnInterface[] = [
    { name: 'cep', displayName: 'CEP' },
    { name: 'bairro', displayName: 'Bairro' },
    { name: 'logradouro', displayName: 'Logradouro' }
  ];
  public showZipCodeSearchResults: boolean;
  public locationInputId = 'localidade-input';
  public possibleLocations: string[];
  public isLoading = true;
  public isLoadingZipCodeAutoFill: boolean;
  public isLoadingZipCodeSearch: boolean;
  public isLoadingPossibleLocations: boolean;
  public error: any;
  public hasError: boolean;

  get zipCodeSearchLocationDisabledStatus() {
    return !this.zipCodeSearchFormGroup.value.estado;
  }

  constructor(
    @Self() public controlDirective: NgControl,
    private addressService: AddressService,
    private errorMessageService: ErrorMessageService,
    private textListInputService: TextListInputService,
    private viewportService: ViewportService
  ) {
    controlDirective.valueAccessor = this;
  }

  ngOnInit() {
    this.isDesktopSubscription = this.viewportService.isDesktopObservable.subscribe((value) => {
      this.isDesktop = value;
    });

    this.formGroup = new UntypedFormGroup({
      cep: new UntypedFormControl(this.value.cep, [Validators.required, Validators.minLength(9)]),
      tipoLogradouro: new UntypedFormControl(this.value.tipoLogradouro, [Validators.required]),
      logradouro: new UntypedFormControl(this.value.logradouro, [Validators.required]),
      numero: new UntypedFormControl(this.value.numero, [Validators.required, Validators.maxLength(9)]),
      numeroCheck: new UntypedFormControl(this.value.numero === '0'),
      complemento: new UntypedFormControl(this.value.complemento),
      bairro: new UntypedFormControl(this.value.bairro, [Validators.required, Validators.minLength(3)]),
      municipio: new UntypedFormControl(this.value.municipio, [Validators.required, Validators.minLength(3)]),
      estado: new UntypedFormControl(this.value.estado, [Validators.required])
    });

    this.formValueChangesSubscription = this.formGroup.valueChanges.subscribe((value) => {
      this.handleInput(value);
    });

    this.zipCodeSearchFormGroup = new UntypedFormGroup({
      estado: new UntypedFormControl(null, [Validators.required]),
      localidade: new UntypedFormControl(null, [Validators.required, Validators.minLength(this.minLocationLength)]),
      logradouro: new UntypedFormControl(null, [Validators.required]),
      numero: new UntypedFormControl(null, [Validators.required, Validators.maxLength(6)])
    });

    this.zipCodeSearchFormValueChangesSubscription = this.zipCodeSearchFormGroup.valueChanges.subscribe(() => {
      this.handleZipCodeSearchInput();
    });

    this.clearTextListInputPossibleValuesSubscription =
      this.textListInputService.clearPossibleValuesObservable.subscribe((value) => {
        if (value === this.locationInputId) {
          this.handleClearTextListInputPossibleValuesEvent();
        }
      });

    this.init();
  }

  ngOnDestroy() {
    if (this.isDesktopSubscription) {
      this.isDesktopSubscription.unsubscribe();
    }

    if (this.formValueChangesSubscription) {
      this.formValueChangesSubscription.unsubscribe();
    }

    if (this.zipCodeSearchFormValueChangesSubscription) {
      this.zipCodeSearchFormValueChangesSubscription.unsubscribe();
    }

    if (this.clearTextListInputPossibleValuesSubscription) {
      this.clearTextListInputPossibleValuesSubscription.unsubscribe();
    }
  }

  private async init() {
    try {
      this.addressTypes = await this.addressService.getAddressTypes();

      this.statesList = await this.addressService.getStatesList();

      if (this.value && this.value.cep) {
        this.handleInput(this.value);
      }
    } catch (error) {
      this.error = error;

      this.hasError = true;

      console.error(error);
    }

    this.isLoading = false;
  }

  private async handleInput(value: AddressInterface) {
    this.value = value;

    this.onTouched();

    await this.checkZipCodeAutoFill();

    this.onChange(this.value);
  }

  private async checkZipCodeAutoFill() {
    if (
      this.value.cep &&
      this.value.cep.length === this.zipCodeFullLength &&
      this.value.cep !== this.lastSearchedZipCode
    ) {
      this.isLoadingZipCodeAutoFill = true;

      this.lastSearchedZipCode = this.value.cep;

      this.autoFilledFields = {};

      try {
        const addressFromZipCode = await this.addressService.getAddressFromZipCode(this.value.cep);

        for (const key in addressFromZipCode) {
          this.value[key] = addressFromZipCode[key] || null;

          this.autoFilledFields[key] = !!addressFromZipCode[key];
        }

        this.formGroup.patchValue(this.value);
      } catch (error) {
        this.errorMessageService.displayErrorToast(error);

        console.error(error);
      }

      this.isLoadingZipCodeAutoFill = false;
    }
  }

  private handleZipCodeSearchInput() {
    clearTimeout(this.zipCodeSearchTimeout);

    this.zipCodeSearchTimeout = setTimeout(() => {
      this.checkPossibleLocations();
    }, 1000);
  }

  private async checkPossibleLocations() {
    const value = this.zipCodeSearchFormGroup.value;

    const state = value.estado;

    const location = value.localidade;

    const selectedListValue = this.textListInputService.getSelectedListValue(this.locationInputId);

    if (
      state &&
      location &&
      location.length >= this.minLocationLength &&
      location !== this.lastSearchedLocation &&
      location !== selectedListValue
    ) {
      this.isLoadingPossibleLocations = true;

      this.lastSearchedLocation = location;

      try {
        this.possibleLocations = await this.addressService.getPossibleLocations(state, location);
      } catch (error) {
        this.errorMessageService.displayErrorToast(error);

        console.error(error);
      }

      this.isLoadingPossibleLocations = false;
    }
  }

  private handleClearTextListInputPossibleValuesEvent() {
    this.possibleLocations = null;

    this.lastSearchedLocation = null;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: any = () => {};

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: any = () => {};

  public writeValue(value: AddressInterface) {
    this.value = value;
    if (this.formGroup) {
      this.formGroup.controls['cep'].setValue(value.cep);
      this.formGroup.controls['tipoLogradouro'].setValue(value.tipoLogradouro);
      this.formGroup.controls['logradouro'].setValue(value.logradouro);
      this.formGroup.controls['numero'].setValue(value.numero);
      this.formGroup.controls['complemento'].setValue(value.complemento);
      this.formGroup.controls['bairro'].setValue(value.bairro);
      this.formGroup.controls['municipio'].setValue(value.municipio);
      this.formGroup.controls['estado'].setValue(value.estado);
    }
  }

  public registerOnChange(fn: any) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public getFieldDisabledStatus(fieldFormControlName: string) {
    return this.autoFilledFields[fieldFormControlName];
  }

  public async runZipCodeSearch() {
    this.isLoadingZipCodeSearch = true;

    try {
      const formValue = this.zipCodeSearchFormGroup.value;

      const state = formValue.estado;
      const location = formValue.localidade;
      const address = formValue.logradouro;
      const number = formValue.numero;

      this.zipCodeSearchResults = await this.addressService.getPossibleAdresses(state, location, address, number);
    } catch (error) {
      this.errorMessageService.displayErrorToast(error);

      console.error(error);
    }

    this.showZipCodeSearchResults = true;

    this.isLoadingZipCodeSearch = false;
  }

  public backZipCodeSearch() {
    this.showZipCodeSearchResults = false;
  }

  public handleZipCodeSearchResultClick(row: Record<string, any>) {
    const zipCodeSearchNumberValue = this.zipCodeSearchFormGroup.get('numero').value;

    this.formGroup.get('numero').setValue(zipCodeSearchNumberValue);

    const zipCode = zipCodeStringParser(row['cep']);

    this.formGroup.get('cep').setValue(zipCode);

    this.closeModal.nativeElement.click();

    setTimeout(() => {
      this.showZipCodeSearchResults = false;
    }, 100);
  }

  get inputCheckNumeroMarked() {
    return this.formGroup.controls['numeroCheck'].value;
  }

  public clickSemNumero() {
    const inputChecked = !this.formGroup.controls['numeroCheck'].value;

    if (!inputChecked) {
      this.formGroup.controls['numero'].setValue('');
    } else {
      this.formGroup.controls['numero'].setValue('0');
    }

    this.formGroup.controls['numero'].updateValueAndValidity();
  }
}
